import { type Dispatch, type FC, type SetStateAction, useContext } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { SymbolIcon, TriggerButton, TriggerButtonGroup } from "widgets";
import { useHotKeys } from "widgets/hooks";

import { FullscreenContext } from "../../app_context.js";
import { type SplitPaneLayoutState } from "../../graphql.js";
import { type IDatasetViewerState, splitPaneLayoutStateChanged } from "../../redux/modules/dataset-viewer.js";
import { type IGenomeBrowserState, featuresTableHideStateChanged } from "../../redux/modules/genome-browser.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { Hotkeys } from "../../settings/hotkeys.js";

export interface IViewPaneLayoutButtonsProps {
  toolbarsState: [boolean, Dispatch<SetStateAction<boolean>>];
  isUiSmall?: boolean;
}

const ViewGenomeViewerLayoutButtons: FC<IViewPaneLayoutButtonsProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { featuresTableLayout } = useSelector<IAppState, IGenomeBrowserState>(
    (state) => state.genomeBrowser,
    shallowEqual,
  );

  const [isFullscreen, setFullscreen] = useContext(FullscreenContext);
  const [showToolbars, setShowToolbars] = props.toolbarsState ?? [true, () => {}];

  const toggleFullscreen = () => {
    setFullscreen(!isFullscreen);
  };

  const toggleToolbars = () => {
    setShowToolbars(!showToolbars);
  };

  const toggleFeaturesTable = () => {
    dispatch(
      featuresTableHideStateChanged({
        ...featuresTableLayout,
        hidden: !featuresTableLayout.verticalLayout && !featuresTableLayout.hidden,
        verticalLayout: featuresTableLayout.hidden
          ? featuresTableLayout.verticalLayout
          : !featuresTableLayout.verticalLayout,
      }),
    );
  };

  useHotKeys(Hotkeys.genomeViewer.toggleFullscreen, toggleFullscreen);
  useHotKeys(Hotkeys.genomeViewer.toggleToolbars, toggleToolbars);
  useHotKeys(Hotkeys.viewPane.splitPaneLayoutChange, () => {
    dispatch(featuresTableHideStateChanged({ ...featuresTableLayout, hidden: true }));
  });
  useHotKeys(Hotkeys.viewPane.toggleTable, toggleFeaturesTable);

  return (
    <TriggerButtonGroup>
      <span id="genome-viewer-features-table-toggle" className="hotspot-beacon" />
      <TriggerButton
        label={getSplitPaneLayoutTooltip(featuresTableLayout, "Features")}
        shortcut={"T"}
        onPress={toggleFeaturesTable}
        id={"genome-viewer-bottom-features-table-toggle"}
        icon={
          featuresTableLayout.verticalLayout ? (
            featuresTableLayout.hidden ? (
              "bottom_panel_open"
            ) : (
              "right_panel_open"
            )
          ) : featuresTableLayout.hidden ? (
            "right_panel_open"
          ) : (
            <SymbolIcon icon="right_panel_close" filled />
          )
        }
      />
      <TriggerButton
        icon={"minimize"}
        label={`${showToolbars ? "Hide" : "Show"} Toolbars`}
        onPress={toggleToolbars}
        shortcut={"N"}
        id={"genome-viewer-toolbars-toggle"}
      />
      <TriggerButton
        icon={isFullscreen ? "fullscreen_exit" : "fullscreen"}
        label={`${isFullscreen ? "Exit" : "Go"} Full Screen`}
        onPress={toggleFullscreen}
        shortcut={"M"}
        id={"genome-viewer-fullscreen-toggle"}
      />
    </TriggerButtonGroup>
  );
};

const getSplitPaneLayoutTooltip = (splitPaneLayout: SplitPaneLayoutState, contentLabel: string) => {
  if (!splitPaneLayout.verticalLayout && !splitPaneLayout.hidden) {
    return `Hide ${contentLabel} Table`;
  } else if (splitPaneLayout.hidden) {
    return splitPaneLayout.verticalLayout
      ? `Show ${contentLabel} Table at the bottom`
      : `Show ${contentLabel} Table on the right side`;
  } else {
    return !splitPaneLayout.verticalLayout
      ? `Show ${contentLabel} Table at the bottom`
      : `Show ${contentLabel} Table on the right side`;
  }
};

const ViewDataViewerLayoutButtons: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { splitPaneLayout } = useSelector<IAppState, IDatasetViewerState>((state) => state.datasetViewer);
  const toggleDataTable = () => {
    dispatch(
      splitPaneLayoutStateChanged({
        ...splitPaneLayout,
        hidden: !splitPaneLayout.verticalLayout && !splitPaneLayout.hidden,
        verticalLayout: splitPaneLayout.hidden ? splitPaneLayout.verticalLayout : !splitPaneLayout.verticalLayout,
      }),
    );
  };
  useHotKeys(Hotkeys.viewPane.splitPaneLayoutChange, () => {
    dispatch(splitPaneLayoutStateChanged({ ...splitPaneLayout, hidden: true }));
  });
  useHotKeys(Hotkeys.viewPane.toggleTable, toggleDataTable);

  return (
    <TriggerButtonGroup>
      <span id="data-viewer-features-table-toggle" className="hotspot-beacon" />
      <TriggerButton
        label={getSplitPaneLayoutTooltip(splitPaneLayout, "Data")}
        shortcut={"T"}
        onPress={toggleDataTable}
        id={"genome-viewer-bottom-features-table-toggle"}
        icon={
          splitPaneLayout.verticalLayout ? (
            splitPaneLayout.hidden ? (
              "bottom_panel_open"
            ) : (
              "right_panel_open"
            )
          ) : splitPaneLayout.hidden ? (
            "right_panel_open"
          ) : (
            <SymbolIcon icon="right_panel_close" filled />
          )
        }
      />
    </TriggerButtonGroup>
  );
};

export { ViewDataViewerLayoutButtons, ViewGenomeViewerLayoutButtons };
